/**
 * Cart Drawer — Luxury Edition (Speed-Optimized v4)
 * --------------------------------------------------
 * v4 changes vs v3:
 * - Checkout button: instant window.location redirect instead of
 *   only adding CSS class. Eliminates slow native form submit chain.
 * - e.preventDefault on checkout click to skip native form post.
 */
(function () {
  'use strict';

  /* ---- Helpers ---- */

  function getRoutesRoot() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return cartUrl.replace(/\/cart\/?$/, '/') || '/';
  }

  function fetchCartJSON() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(cartUrl + '.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  function refreshDrawerSections() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(cartUrl + '?section_id=cart-drawer', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (responseText) {
        var html = new DOMParser().parseFromString(responseText, 'text/html');
        ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (sel) {
          var target = document.querySelector(sel);
          var source = html.querySelector(sel);
          if (target && source) target.replaceWith(source);
        });
      })
      .catch(function () {});
  }

  function refreshCartIcon() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(cartUrl + '?section_id=cart-icon-bubble', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (responseText) {
        var html = new DOMParser().parseFromString(responseText, 'text/html');
        var wrapper = html.querySelector('.shopify-section');
        var target = document.getElementById('cart-icon-bubble');
        if (wrapper && target) target.innerHTML = wrapper.innerHTML;
      })
      .catch(function () {});
  }

  function toggleEmptyState(itemCount) {
    var cartDrawer = document.querySelector('cart-drawer');
    var cartFooter = document.getElementById('main-cart-footer');
    if (cartDrawer) cartDrawer.classList.toggle('is-empty', itemCount === 0);
    if (cartFooter) cartFooter.classList.toggle('is-empty', itemCount === 0);
  }

  /* ================================================================
     Discount code — fully delegated, parallel requests
     ================================================================ */

  function applyDiscount(wrapper) {
    var input = wrapper.querySelector('[data-discount-input]');
    var button = wrapper.querySelector('[data-discount-apply]');
    var message = wrapper.querySelector('[data-discount-message]');

    function setMessage(text, state) {
      if (!message) return;
      message.textContent = text;
      message.hidden = !text;
      if (state) { message.setAttribute('data-state', state); }
      else { message.removeAttribute('data-state'); }
    }

    var code = (input && input.value || '').trim();
    if (!code) {
      setMessage(window.VETTA_US ? 'Please enter a discount code.' : 'Bitte einen Rabattcode eingeben.', 'error');
      return;
    }

    if (button) {
      button.disabled = true;
      button._origLabel = button._origLabel || button.textContent;
      button.textContent = '…';
    }
    setMessage('', null);

    var root = getRoutesRoot();
    var discountUrl = root + 'discount/' + encodeURIComponent(code) +
      '?redirect=' + encodeURIComponent(root + 'cart');

    fetch(discountUrl, { credentials: 'same-origin' })
      .catch(function () {})
      .then(function () {
        return Promise.all([refreshDrawerSections(), fetchCartJSON()]);
      })
      .then(function (results) {
        var cartAfter = results[1];
        var codes = cartAfter.discount_codes || [];
        var discountEntry = codes.filter(function (d) {
          return d.code && d.code.trim().toLowerCase() === code.trim().toLowerCase();
        }).pop();
        var applied = !!discountEntry && discountEntry.applicable !== false;

        if (!applied && (cartAfter.total_discount || 0) > 0) {
          applied = true;
        }

        if (applied) {
          setMessage(window.VETTA_US ? 'Code applied.' : 'Code angewendet.', 'success');
          if (input) input.value = '';
        } else if (discountEntry && discountEntry.applicable === false) {
          setMessage(window.VETTA_US ? 'Code not applicable to this order.' : 'Code nicht anwendbar auf diese Bestellung.', 'error');
        } else {
          setMessage(window.VETTA_US ? 'Invalid or inapplicable code.' : 'Code ungültig oder nicht anwendbar.', 'error');
        }
      })
      .catch(function () {
        setMessage(window.VETTA_US ? 'Code could not be applied.' : 'Code konnte nicht angewendet werden.', 'error');
      })
      .finally(function () {
        if (button) {
          button.disabled = false;
          button.textContent = button._origLabel || (window.VETTA_US ? 'Apply' : 'Anwenden');
        }
      });
  }

  /* ================================================================
     Upsell add-to-cart — delegated
     ================================================================ */

  function handleUpsellAdd(button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) return;

    var originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = '…';

    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw new Error(data.description || data.message || 'Fehler');
          return data;
        });
      })
      .then(function () {
        return Promise.all([refreshDrawerSections(), refreshCartIcon(), fetchCartJSON()]);
      })
      .then(function (results) {
        var cart = results[2];
        if (cart) toggleEmptyState(cart.item_count);
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function' && !drawer.classList.contains('active')) {
          drawer.open();
        }
        button.textContent = window.VETTA_US ? 'Added ✓' : 'Hinzugefügt ✓';
      })
      .catch(function () {
        button.textContent = window.VETTA_US ? 'Error' : 'Fehler';
      })
      .finally(function () {
        setTimeout(function () {
          button.textContent = originalLabel;
          button.disabled = false;
        }, 1400);
      });
  }

  /* ================================================================
     Checkout button — instant visual feedback + fast redirect
     ================================================================ */

  function handleCheckout(btn) {
    if (btn.disabled || btn.classList.contains('loading')) return;
    btn.classList.add('loading');
    btn.setAttribute('aria-busy', 'true');
    btn.style.pointerEvents = 'none';
    /* Instant redirect to checkout — skip slow native form submit */
    var checkoutUrl = (window.routes && window.routes.checkout_url) || '/checkout';
    window.location.href = checkoutUrl;
  }

  /* ================================================================
     Optimistic cart drawer open on add-to-cart
     ================================================================ */

  function initOptimisticDrawerOpen() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches('form[action*="/cart/add"]') || form.closest('[data-ic-configurator]')) return;
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        setTimeout(function () {
          if (!drawer.classList.contains('active')) {
            drawer.open();
          }
        }, 50);
      }
    }, true);
  }

  /* ================================================================
     Global event delegation — survives section refreshes
     ================================================================ */

  document.addEventListener('click', function (e) {
    var target = e.target;

    /* Discount "Anwenden" button */
    var discountBtn = target.closest('[data-discount-apply]');
    if (discountBtn) {
      e.preventDefault();
      var wrapper = discountBtn.closest('.cart-drawer-discount');
      if (wrapper) applyDiscount(wrapper);
      return;
    }

    /* Upsell "Hinzufügen" button */
    var upsellBtn = target.closest('[data-upsell-add]');
    if (upsellBtn) {
      handleUpsellAdd(upsellBtn);
      return;
    }

    /* Checkout button instant feedback + redirect */
    var checkoutBtn = target.closest('.cart__checkout-button');
    if (checkoutBtn) {
      e.preventDefault();
      handleCheckout(checkoutBtn);
      return;
    }
  }, true);

  /* Discount input Enter key — delegated */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest('[data-discount-input]');
    if (!input) return;
    e.preventDefault();
    var wrapper = input.closest('.cart-drawer-discount');
    if (wrapper) applyDiscount(wrapper);
  }, true);

  /* ================================================================
     Init
     ================================================================ */

  function init() {
    initOptimisticDrawerOpen();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
